import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { api, pageGenerationInFlight, type Book, type Job, type Page } from "../api";
import Compose from "./Compose";
import Processing from "./Processing";
import Reader from "./Reader";

/**
 * Single route /books/:id that picks a screen based on book.status:
 *   draft               → Compose
 *   queued | polishing  → Processing (polls every 2s)
 *   ready | failed      → Reader / Processing-with-error
 */
export default function BookPage() {
  const { t } = useTranslation();
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<{ book: Book; pages: Page[]; job: Job | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const load = async () => {
    if (!bookId) return;
    try {
      const data = await api.getBook(bookId);
      setState(data);
    } catch (e) {
      setErr(String(e));
    }
  };

  useEffect(() => {
    void load();
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  // Poll while polish is in flight so progress / completion arrives without
  // a manual refresh.
  useEffect(() => {
    if (!state) return;
    const arr = state.book.activeReplaceRevision;
    const inFlight =
      state.book.status === "queued" ||
      state.book.status === "polishing" ||
      state.pages.some((p) => pageGenerationInFlight(p.latestGeneration)) ||
      (arr != null && arr.status !== "failed");
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (inFlight) {
      pollRef.current = window.setInterval(() => {
        void load();
      }, 2000);
    }
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.book.status, state?.pages, state?.book.activeReplaceRevision?.status]);

  useEffect(() => {
    if (state?.book.replacedByBookId) {
      navigate(`/app/books/${state.book.replacedByBookId}`, { replace: true });
    }
  }, [state?.book.replacedByBookId, navigate]);

  if (err) {
    return (
      <div className="tp-app">
        <div className="tp-screen" style={{ paddingTop: 80 }}>
          <div className="tp-h1" style={{ marginBottom: 8 }}>{t("bookPage.errorTitle")}</div>
          <div className="tp-body tp-soft" style={{ marginBottom: 16 }}>{err}</div>
          <button className="tp-btn tp-btn-block" onClick={() => navigate("/app")}>{t("bookPage.backToLibrary")}</button>
        </div>
      </div>
    );
  }
  if (!state) return <div className="tp-app"><div className="tp-loading">{t("common.loading")}</div></div>;

  const { book, pages, job } = state;
  if (book.status === "draft") {
    if (book.mode === "story") {
      // Should not normally happen — story books are queued at create
      // time. If we land here, the book is half-created; bounce to
      // /new/story so the user can start over.
      return (
        <div className="tp-app">
          <div className="tp-screen" style={{ paddingTop: 80 }}>
            <div className="tp-h1" style={{ marginBottom: 8 }}>{t("bookPage.notReadyTitle")}</div>
            <div className="tp-body tp-soft" style={{ marginBottom: 16 }}>{t("bookPage.notReadyBody")}</div>
            <button className="tp-btn tp-btn-block" onClick={() => navigate("/app/new/story")}>{t("common.retry")}</button>
          </div>
        </div>
      );
    }
    return (
      <Compose
        book={book}
        pages={pages}
        onBack={() => navigate("/app")}
        onSubmitted={(submitted) =>
          setState((prev) => (prev ? { ...prev, book: submitted.book, job: submitted.job } : prev))
        }
      />
    );
  }
  if (book.status === "queued" || book.status === "polishing" || book.status === "failed") {
    return <Processing book={book} job={job} onRetried={load} />;
  }
  // Beacon the author's own reads too (server records them under the account,
  // so they fold into the same view count without inflating unique readers).
  return (
    <Reader book={book} pages={pages} ownerControls trackShortId={book.shortId} onRefresh={load} backTo="/app" />
  );
}
